import { z, ZodSchema } from 'zod';
import { EventType } from './common';
import * as Sentry from '@sentry/browser';
import { SUBSCRIPTION_TYPES } from '$lib/utils/constants';

const MetaSchema = z.object({
	isIosViewer: z.boolean().nullable(),
	viewerPostalCode: z.string().nullable(),
	viewerLongitude: z.string().nullable(),
	viewerLatitude: z.string().nullable(),
	viewerAsn: z.string().nullable(),
	viewerMetroCode: z.string().nullable(),
	viewerCountryRegion: z.string().nullable(),
	isAndroidViewer: z.boolean().nullable(),
	inApp: z.boolean().nullable(),
	viewerCity: z.string().nullable(),
	viewerCountryName: z.string().nullable(),
	viewerTls: z.string().nullable(),
	forwardedProto: z.string().nullable(),
	viewerCountryRegionName: z.string().nullable(),
	viewerCountry: z.string(),
	appVersion: z.string().nullable(),
	viewerAddress: z.string().nullable(),
	isDesktopViewer: z.boolean().nullable(),
	isMobileViewer: z.boolean().nullable(),
	viewerHttpVersion: z.string().nullable(),
	viewerTimeZone: z.string().nullable(),
	isSmarttvViewer: z.string().nullable(),
	isTabletViewer: z.string().nullable(),
	isAndroidApp: z.boolean().nullable(),
	isSmartTvApp: z.boolean().nullable(),
	isAmazonApp: z.boolean().nullable(),
	isIosApp: z.boolean().nullable(),
	isSamsungApp: z.boolean().nullable(),
	isMac: z.boolean().nullable(),
	isWindows: z.boolean().nullable(),
	isLinkFromApp: z.boolean().nullable(),
	isAmazonViewer: z.boolean().nullable(),
	localeCode: z.string()
});

const EventSchema = z.object({
	type: z.nativeEnum(EventType),
	meta: MetaSchema,
	deviceId: z.string().min(1),
	aoflProductId: z.string().min(1),
	detail: z.object({}) // This will be overridden in child schemas
});

const AppLoadEventDetailSchema = z.object({
  sourceTag: z.union([z.string(), z.object({})]), // sourceTag can be a string or an object
  dynamicInfo: z.record(z.any()).optional()
});

const ClickEventDetailSchema = z.object({
  linkId: z.string().min(1),
  experimentId: z.string().nullable(),
  appVersion: z.string().min(1),
  elementPath: z.string().min(1),
  pageName: z.string().min(1),
  siteSection: z.string().min(1)
});

const LoadEventDetailSchema = z.object({
	source: z.string().min(1),
	urlParams: z.string(),
	pageName: z.string().min(1),
	pageURL: z.string().min(1),
	viewInfo: z.array(z.string())
});

const SubscribeEventDetailSchema = z.object({
	userToProductId: z.string().min(1),
	productHash: z.string().min(1),
	productPrice: z.string().min(1),
	productCurrency: z.string().min(1),
	lTV: z.string().min(1).optional(), // Resub page does not have lTV event
	subscriptionType: z.enum([
		SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION,
		SUBSCRIPTION_TYPES.EXISTING_SUBSCRIPTION
	])
});

// Extend EventSchema with AppLoadEvent
const AppLoadEventSchema = EventSchema.extend({
  type: z.literal(EventType.AppLoad),
  detail: AppLoadEventDetailSchema
});

const ClickEventSchema = EventSchema.extend({
  type: z.literal(EventType.Click),
  detail: ClickEventDetailSchema
});

const LoadEventSchema = EventSchema.extend({
	type: z.literal(EventType.PageLoad),
	detail: LoadEventDetailSchema
});

const SubscribeEventSchema = EventSchema.extend({
	type: z.literal(EventType.Subscribe),
	detail: SubscribeEventDetailSchema
});

const UpgradeEventSchema = EventSchema.extend({
	type: z.literal(EventType.Upgrade),
	detail: SubscribeEventDetailSchema
});

const AuxiliarySubscriptionEventSchema = EventSchema.extend({
	type: z.literal(EventType.Auxiliary),
	detail: SubscribeEventDetailSchema
});

/**
 * @param {object} event - the event in question being validated
 * @param {unknown} error - error from zod
 */
function logValidationFailure(event, error) {
	Sentry.captureException(`Event Validation Failure`, {
		extra: {
			event,
			error
		}
	});
}

/**
 * Generates a load event detail object with default values.
 * @param {import('./types').Events.LoadEventDetail} evtDetail - input event
 * @returns {import('./types').Events.LoadEventDetail} The load event detail object with default values.
 */
const generateLoadEventDetailWithDefaultValues = (evtDetail) => {
	return {
		source: evtDetail?.source || 'UnknownSource',
		urlParams: evtDetail?.urlParams || 'UnknownParams',
		pageName: evtDetail?.pageName || 'UnknownPageName',
		pageURL: evtDetail?.pageURL || 'UnknownPageURL',
		viewInfo: evtDetail?.viewInfo || ['']
	};
};

/**
 * Generates a load event detail object with default values.
 * @param {import('./types').Events.SubscribeEventDetail} evtDetail - input event
 * @returns {import('./types').Events.SubscribeEventDetail} The load event detail object with default values.
 */
const generateSubscribeEventDetailWithDefaultValues = (evtDetail) => {
	return {
		userToProductId: evtDetail?.userToProductId || 'UnknownUserToProductId',
		productHash: evtDetail?.productHash || 'UnknownProductHash',
		productPrice: evtDetail?.productPrice || '0.00',
		productCurrency: evtDetail?.productCurrency || 'USD',
		subscriptionType: evtDetail?.subscriptionType || SUBSCRIPTION_TYPES.NEW_SUBSCRIPTION
	};
};

/**
 * Validates an event against a Zod schema and merges it with default schema values if validation fails.
 * @param {import('./types').Events.Event<EventType, object>} event - The event object to validate.
 * @param {ZodSchema} schema - The Zod schema to validate the event against.
 * @returns {import('./types').Events.Event<EventType, object>} - The parsed event, or a combination of default schema values and the event.
 */
export function validateSchema(event, schema) {
	const parsedResult = schema.safeParse(event);
	// @ts-ignore // TODO
	if (parsedResult.success) return parsedResult.data;
	else {
		logValidationFailure(event, parsedResult.error);
		const defaultField =
			event.type === EventType.PageLoad
				// @ts-ignore // TODO
				? generateLoadEventDetailWithDefaultValues(event.detail)
				// @ts-ignore // TODO
				: generateSubscribeEventDetailWithDefaultValues(event.detail);

		return { ...event, detail: { ...defaultField } };
	}
}

/**
 * Validates a load event against the LoadEventSchema.
 * @param {import('./types').Events.Event<EventType, object>} event - The load event to validate.
 * @returns {import('./types').Events.Event<EventType, object>} - The validated load event or a combination of default values and the event.
 */
export function validatePageLoadEvent(event) {
	return validateSchema(event, LoadEventSchema);
}

/**
 * Validates a subscribe event against the SubscribeEventSchema.
 * @param {import('./types').Events.Event<EventType, object>} event - The load event to validate.
 * @returns {import('./types').Events.Event<EventType, object>} - The validated subscribe event or a combination of default values and the event.
 */
export function validateSubscribeEvent(event) {
	return validateSchema(event, SubscribeEventSchema);
}

/**
 * Validates an upgrade event against the UpgradeEventSchema.
 * @param {import('./types').Events.Event<EventType, object>} event - The load event to validate.
 * @returns {import('./types').Events.Event<EventType, object>} - The validated upgrade event or a combination of default values and the event.
 */
export function validateUpgradeEvent(event) {
	return validateSchema(event, UpgradeEventSchema);
}

/**
 * Validates an campaign attribution event against the AppLoadEventSchema.
 * @param {import('./types').Events.Event<EventType, object>} event - The load event to validate.
 * @returns {import('./types').Events.Event<EventType, object>} - The validated campaign attribution event or a combination of default values and the event.
 */
export function validateAppLoadEvent(event) {
  return validateSchema(event, AppLoadEventSchema);
}

/**
 * Validates an click event against the ClickEventSchema.
 * @param {import('./types').Events.Event<EventType, object>} event - The load event to validate.
 * @returns {import('./types').Events.Event<EventType, object>} - The validated click event or a combination of default values and the event.
 */
export function validateClickEvent(event) {
  return validateSchema(event, ClickEventSchema);
}

/**
 * Validates an auxiliary subscription event against the AuxiliarySubscriptionEventSchema.
 * @param {import('./types').Events.Event<EventType, object>} event - The load event to validate.
 * @returns {import('./types').Events.Event<EventType, object>} - The validated auxiliary subscription event or a combination of default values and the event.
 */
export function validateAuxiliarySubscriptionEvent(event) {
	return validateSchema(event, AuxiliarySubscriptionEventSchema);
}
